import type React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import Input from "components/v2/Input/Input";
import { classNames } from "helpers/commonHelpers";

import "./DimensionFieldInput.scss";

type DimensionFieldInputProps = {
  name: string;
  dimensionLabel: string;
  showOptionalHint?: boolean;
  showRequiredHint?: boolean;
  onChange?: () => void;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
};

export default function DimensionFieldInput({
  name,
  dimensionLabel,
  showOptionalHint = false,
  showRequiredHint = false,
  onChange,
  onPaste,
}: Readonly<DimensionFieldInputProps>) {
  const intl = useIntl();
  const { control } = useFormContext();
  const value = useWatch({ name, control });
  const hasValue = value !== null && value !== undefined && value !== "";
  const showRequiredOverlay = showRequiredHint && !hasValue;
  const showOptionalOverlay = showOptionalHint && !showRequiredOverlay && !hasValue;
  const showPlaceholder = showOptionalOverlay || showRequiredOverlay;

  return (
    <div
      className={classNames("rfv2-dimension-field-input", {
        "rfv2-dimension-field-input--optional": showOptionalOverlay,
        "rfv2-dimension-field-input--required": showRequiredOverlay,
        "rfv2-dimension-field-input--has-value": hasValue,
      })}
    >
      <Input
        name={name}
        placeholder={showPlaceholder ? "" : dimensionLabel}
        type="number"
        hideError={showRequiredOverlay}
        onChange={onChange}
        onPaste={onPaste}
      />
      {showPlaceholder && (
        <div className="rfv2-dimension-field-input__placeholder" aria-hidden="true">
          <span className="rfv2-dimension-field-input__label">{dimensionLabel}</span>
          {showOptionalOverlay && (
            <span className="rfv2-dimension-field-input__optional">
              {intl.formatMessage({
                id: "request-form.packing-list.dimensionOptional",
                defaultMessage: "optional",
              })}
            </span>
          )}
        </div>
      )}
      {showRequiredOverlay && (
        <span className="rfv2-dimension-field-input__required">
          {intl.formatMessage({
            id: "request-form.packing-list.requiredHint",
            defaultMessage: "*Required",
          })}
        </span>
      )}
    </div>
  );
}
